import React, { useEffect, useMemo } from 'react';
import { OptionProps, SelectWithLabel } from '@/components/shared';
import { useSegmentation } from '@/contexts/SegmentationContext';
import { useListApplicationRegistrationsWithoutFilterUnit } from '@/generated/application-registrations';
import { useGetTranslations } from '@/generated/translations';
import styles from './MoveModal.module.scss';

export interface MoveFilterUnitFieldsProps {
	accountId: number;
	/**
	 * Set when the location moves along with the filter unit: a target that already
	 * holds one has no room for it, and the move endpoint answers 409, so such
	 * targets are left out of the list.
	 */
	withoutLocation?: boolean;
	value: number | null;
	onChange: (targetRegistrationCompositeId: number | null) => void;
}

/**
 * Body shown when either "move filter unit" option is selected.
 *
 * Lists the registrations on this account that do not already hold a filter
 * unit, and optionally only those without a location. Registrations holding
 * only a location have no application and so no name; they are still valid
 * targets and are labelled by their location identifier. The backend scopes
 * the list to the account and excludes composites that have a filter unit, so
 * the current composite can never appear.
 */
export const MoveFilterUnitFields: React.FunctionComponent<MoveFilterUnitFieldsProps> = ({
	accountId,
	withoutLocation = false,
	value,
	onChange,
}) => {
	const { segmentationId } = useSegmentation();
	const { data: translations } = useGetTranslations(segmentationId);
	const {
		data: response,
		isLoading,
		isError,
	} = useListApplicationRegistrationsWithoutFilterUnit(segmentationId, accountId, {
		WithoutLocation: withoutLocation,
	});

	const moveTranslations = translations?.data.composite.moveComposite;

	const applications = response?.data;

	const options: OptionProps[] = useMemo(() => {
		const placeholder: OptionProps = {
			displayName: moveTranslations?.selectApplication ?? '',
			// Must be '' rather than undefined: React omits an undefined value
			// attribute, so the option's value falls back to its own text, which
			// both stops the empty select value matching it and turns re-selecting
			// it into Number('Select an application') === NaN.
			value: '',
		};

		const mapped = (applications ?? []).map(
			({ registrationCompositeId, name, application, oilSystem, locationIdentifier }): OptionProps => ({
				// Every text field is nullable, so fall back to the location and then the id.
				displayName:
					[name, application, oilSystem].filter(Boolean).join(' - ') ||
					locationIdentifier ||
					`#${registrationCompositeId}`,
				value: String(registrationCompositeId),
			}),
		);

		return [placeholder, ...mapped];
	}, [applications, moveTranslations]);

	// The list refetches on its own (5s stale time, plus on window focus), and a
	// composite drops out as soon as it gains a filter unit. Without this the
	// select would render blank while Confirm stayed enabled for the vanished
	// option, and confirming would be rejected by the backend.
	useEffect(() => {
		if (value === null || !applications) {
			return;
		}

		if (!applications.some((application) => application.registrationCompositeId === value)) {
			onChange(null);
		}
	}, [applications, value, onChange]);

	// Always render something — a silent empty body looks like a broken modal.
	if (isLoading) {
		return <p className={styles.emptyMessage}>{translations?.data.shared.loading}</p>;
	}

	if (isError) {
		return <p className={styles.emptyMessage}>{translations?.data.shared.genericErrorMsg}</p>;
	}

	if (!applications?.length) {
		return (
			<p className={styles.emptyMessage}>
				{withoutLocation
					? moveTranslations?.noApplicationsWithoutLocationAvailable
					: moveTranslations?.noApplicationsAvailable}
			</p>
		);
	}

	return (
		<div className={styles.field}>
			<SelectWithLabel
				id={'move-target-application'}
				name={'targetRegistrationCompositeId'}
				ariaLabel={translations?.data.applicationRegistrations.chooseApplication ?? ''}
				options={options}
				value={value === null ? '' : String(value)}
				variant={'inverse'}
				onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
			/>
		</div>
	);
};
